import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  ConversationApiService,
  GetMultiMessagesRequest,
  ListMessagesRequest,
  ListMessagesRequestListMessagesFilters,
  Message,
  PagedRequestOptions,
} from '@vendasta/conversation';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  retry,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { toFirestoreId } from '../conversation-utils';
import { FirestoreService } from '../firestore.service';

interface FirestoreMessageSummary {
  messageId?: string;
  id?: string;
  created?: Date | { toMillis?: () => number };
  updated?: Date | { toMillis?: () => number };
}

const PAGE_SIZE = 50;
const FIRESTORE_DEBOUNCE_MS = 200;
const SENTINEL_COOLDOWN_MS = 500;
const RETRY_DELAYS_MS = [500, 1000, 2000];
const MAX_HYDRATION_PER_CYCLE = PAGE_SIZE;

type LoadOlderSource = 'user' | 'prefetch';

export interface PaginatedMessagesState {
  conversationId: string;
  messages: ReadonlyMap<string, Message>;
  orderedIds: ReadonlyArray<string>;
  nextCursor: string | null;
  hasMore: boolean;
  loadingFirstPage: boolean;
  loadingOlder: boolean;
  lastPageLandedAt: number;
}

const emptyState = (conversationId: string): PaginatedMessagesState => ({
  conversationId,
  messages: new Map(),
  orderedIds: [],
  nextCursor: '',
  hasMore: true,
  loadingFirstPage: !!conversationId,
  loadingOlder: false,
  lastPageLandedAt: 0,
});

/**
 * Paginated chat-view state machine for the `paginated_chat_view` path.
 *
 * Lifecycle invariant: this service is module-scoped (singleton across the
 * inbox), so ngOnDestroy does not fire on conversation switch. The single
 * lifecycle handle is `setConversationId(id)` — calling it resets every
 * per-conversation field (state, firestoreBaseline, inFlightHydrationIds,
 * latestFsSnapshot) and re-binds the Firestore subscription. Passing an
 * empty id releases state and stops all API calls. Any consumer that
 * bypasses this entry point will read across conversations. ngOnDestroy is
 * wired for an eventual move to component-scoped DI.
 */
@Injectable()
export class PaginatedConversationMessagesService implements OnDestroy {
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly firestoreService = inject(FirestoreService);

  private readonly state$$ = new BehaviorSubject<PaginatedMessagesState>(emptyState(''));
  private readonly conversationId$$ = new BehaviorSubject<string>('');
  private readonly loadOlder$$ = new Subject<LoadOlderSource>();
  private readonly destroy$$ = new Subject<void>();
  private readonly drainTick$$ = new Subject<void>();
  private firestoreBaseline: Set<string> | null = null;
  private readonly firestoreUpdatedAtMs = new Map<string, number>();
  private readonly inFlightHydrationIds = new Set<string>();
  private latestFsSnapshot: FirestoreMessageSummary[] = [];
  private initialPageLanded = false;

  readonly state$: Observable<PaginatedMessagesState> = this.state$$.asObservable();

  readonly messages$: Observable<Message[]> = this.state$.pipe(
    distinctUntilChanged((a, b) => a.orderedIds === b.orderedIds),
    map((s) => s.orderedIds.map((id) => s.messages.get(id)).filter((m): m is Message => !!m)),
  );

  readonly loadingFirstPage$ = this.state$.pipe(
    map((s) => s.loadingFirstPage),
    distinctUntilChanged(),
  );
  readonly loadingOlder$ = this.state$.pipe(
    map((s) => s.loadingOlder),
    distinctUntilChanged(),
  );
  readonly hasMore$ = this.state$.pipe(
    map((s) => s.hasMore),
    distinctUntilChanged(),
  );

  constructor() {
    this.conversationId$$
      .pipe(
        switchMap((conversationId) => {
          this.state$$.next(emptyState(conversationId));
          this.firestoreBaseline = null;
          this.firestoreUpdatedAtMs.clear();
          this.inFlightHydrationIds.clear();
          this.latestFsSnapshot = [];
          this.initialPageLanded = false;
          if (!conversationId) return EMPTY;
          return this.startPipeline(conversationId);
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe();
  }

  setConversationId(conversationId: string): void {
    if (conversationId !== this.conversationId$$.value) {
      this.conversationId$$.next(conversationId);
    }
  }

  loadOlder(): void {
    this.loadOlder$$.next('user');
  }

  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private startPipeline(conversationId: string): Observable<unknown> {
    const initial$ = this.fetchPage(conversationId, '').pipe(
      tap((page) => this.applyPage(page, 'initial')),
      catchError((err) => {
        // No user-facing retry surface today; tracked in BREW-2597. Clear the
        // spinner so it doesn't hang.
        console.error('[paginated-messages] initial page failed after retries', err);
        this.state$$.next({ ...this.state$$.value, loadingFirstPage: false });
        return EMPTY;
      }),
    );

    const older$ = this.loadOlder$$.pipe(
      filter((source) => (source === 'prefetch' ? this.canPrefetchOlder() : this.canLoadOlder())),
      switchMap((source) => {
        const s = this.state$$.value;
        this.state$$.next({ ...s, loadingOlder: true });
        return this.fetchPage(conversationId, s.nextCursor || '').pipe(
          tap((page) => this.applyPage(page, 'older')),
          catchError((err) => {
            // Next user-driven loadOlder will re-attempt with the same cursor.
            console.warn(`[paginated-messages] older page failed (source=${source})`, err);
            this.state$$.next({ ...this.state$$.value, loadingOlder: false });
            return EMPTY;
          }),
        );
      }),
    );

    const fsSource$ = this.firestoreService.messages$(toFirestoreId(conversationId)).pipe(
      catchError((err) => {
        console.error('[paginated-messages] firestore stream errored', err);
        return EMPTY;
      }),
      debounceTime(FIRESTORE_DEBOUNCE_MS),
      tap((snap) => (this.latestFsSnapshot = snap)),
    );
    const firestore$ = merge(
      fsSource$,
      this.drainTick$$.pipe(map(() => this.latestFsSnapshot)),
    ).pipe(
      // switchMap cancels any in-flight hydrate on a fresh snapshot. Under
      // heavy churn we may cancel + re-issue getMultiMessages for the same
      // ids; the response payload is identical and inFlightHydrationIds
      // prevents simultaneous duplicates, so this is acceptable.
      switchMap((fsMsgs: FirestoreMessageSummary[]) => {
        const { ids, capped } = this.collectIdsToHydrate(fsMsgs);
        if (ids.length === 0) return EMPTY;
        ids.forEach((id) => this.inFlightHydrationIds.add(id));
        return this.hydrate(conversationId, ids).pipe(
          tap((hydrated) => this.applyHydration(hydrated)),
          catchError((err) => {
            console.error('[paginated-messages] hydration failed after retries', err);
            return EMPTY;
          }),
          finalize(() => {
            ids.forEach((id) => this.inFlightHydrationIds.delete(id));
            if (capped) this.drainTick$$.next();
          }),
        );
      }),
    );

    // older$ must subscribe before initial$ runs: the prefetch emission fires
    // synchronously from applyPage('initial') and would be dropped if older$
    // were not yet listening.
    return merge(older$, firestore$, initial$);
  }

  private canLoadOlder(): boolean {
    const s = this.state$$.value;
    return (
      !s.loadingOlder &&
      !s.loadingFirstPage &&
      s.hasMore &&
      !!s.nextCursor &&
      Date.now() - s.lastPageLandedAt > SENTINEL_COOLDOWN_MS
    );
  }

  // Prefetch fires programmatically once after the initial page lands, not
  // from a layout-shift loop, so the sentinel cooldown does not apply.
  private canPrefetchOlder(): boolean {
    const s = this.state$$.value;
    return !s.loadingOlder && !s.loadingFirstPage && s.hasMore && !!s.nextCursor;
  }

  private fetchPage(
    conversationId: string,
    cursor: string,
  ): Observable<{ messages: Message[]; nextCursor: string; hasMore: boolean }> {
    return defer(() => {
      const req = new ListMessagesRequest({
        filters: new ListMessagesRequestListMessagesFilters({ conversationId }),
        pagingOptions: new PagedRequestOptions({ cursor, pageSize: PAGE_SIZE }),
      });
      return this.conversationApiService.listMessages(req);
    }).pipe(
      retry({
        count: RETRY_DELAYS_MS.length,
        delay: (_err, attempt) => timer(RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)]),
      }),
      map((resp) => ({
        messages: (resp.messages || []) as Message[],
        nextCursor: resp.pagingMetadata?.nextCursor || '',
        hasMore: !!resp.pagingMetadata?.hasMore,
      })),
    );
  }

  private hydrate(conversationId: string, ids: string[]): Observable<Message[]> {
    return defer(() =>
      this.conversationApiService.getMultiMessages(new GetMultiMessagesRequest({ conversationId, messageIds: ids })),
    ).pipe(
      retry({
        count: RETRY_DELAYS_MS.length,
        delay: (_err, attempt) => timer(RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)]),
      }),
      map((resp) => (resp.messages || []) as Message[]),
    );
  }

  // Baseline = pre-existing ids (in the initial page, or older than its
  // newest message); these load via scroll-up ListMessages, not bulk
  // hydration. We wait for the initial page to land before establishing
  // baseline so Firestore-only ids whose `created` is newer than the
  // initial page's newest are detected as post-snapshot arrivals and
  // hydrated. Without this, a message that arrived between the
  // ListMessages snapshot and the first FS emit would be buried in baseline
  // and never surface; and opening a 5k+ message thread would fan out into
  // 5k+ getMultiMessages calls.
  //
  // Known ids whose Firestore `updated` advanced past what we last hydrated
  // are re-included so sendStatus/aiStatus mutations propagate.
  private collectIdsToHydrate(fsMsgs: FirestoreMessageSummary[]): { ids: string[]; capped: boolean } {
    const summaries = this.extractSummaries(fsMsgs);
    if (this.firestoreBaseline === null) {
      if (!this.initialPageLanded) return { ids: [], capped: false };
      return this.establishBaseline(summaries);
    }
    const known = this.state$$.value.messages;
    const out: string[] = [];
    const seen = new Set<string>();
    let capped = false;
    for (const { id, updatedMs } of summaries) {
      if (this.inFlightHydrationIds.has(id)) continue;
      if (seen.has(id)) continue;
      const isNew = !this.firestoreBaseline.has(id) && !known.has(id);
      const lastKnownMs = this.firestoreUpdatedAtMs.get(id) ?? 0;
      const isMutated = updatedMs > 0 && updatedMs > lastKnownMs;
      if (!isNew && !isMutated) continue;
      if (out.length >= MAX_HYDRATION_PER_CYCLE) {
        capped = true;
        break;
      }
      seen.add(id);
      out.push(id);
      if (updatedMs > 0) this.firestoreUpdatedAtMs.set(id, updatedMs);
    }
    return { ids: out, capped };
  }

  private establishBaseline(
    summaries: { id: string; updatedMs: number; createdMs: number }[],
  ): { ids: string[]; capped: boolean } {
    const known = this.state$$.value.messages;
    const newestInitialMs = this.newestCreatedMs(known);
    this.firestoreBaseline = new Set<string>();
    const ids: string[] = [];
    const seen = new Set<string>();
    let capped = false;
    for (const { id, updatedMs, createdMs } of summaries) {
      // Strict `<` so a post-snapshot arrival sharing a created-ms with the
      // initial page's newest still hydrates instead of being buried in
      // baseline. Listed ids are caught by known.has(id) above.
      const preExisting = known.has(id) || createdMs < newestInitialMs;
      if (preExisting) {
        this.firestoreBaseline.add(id);
        if (updatedMs > 0) this.firestoreUpdatedAtMs.set(id, updatedMs);
        continue;
      }
      if (this.inFlightHydrationIds.has(id)) continue;
      if (seen.has(id)) continue;
      if (ids.length >= MAX_HYDRATION_PER_CYCLE) {
        capped = true;
        continue;
      }
      seen.add(id);
      ids.push(id);
      if (updatedMs > 0) this.firestoreUpdatedAtMs.set(id, updatedMs);
    }
    return { ids, capped };
  }

  private newestCreatedMs(messages: ReadonlyMap<string, Message>): number {
    let max = 0;
    for (const m of messages.values()) {
      const t = m.created?.getTime() ?? 0;
      if (t > max) max = t;
    }
    return max;
  }

  private extractSummaries(
    fsMsgs: FirestoreMessageSummary[],
  ): { id: string; updatedMs: number; createdMs: number }[] {
    const out: { id: string; updatedMs: number; createdMs: number }[] = [];
    for (const m of fsMsgs) {
      const id = m.messageId || m.id;
      if (!id) continue;
      out.push({ id, updatedMs: this.toMillis(m.updated), createdMs: this.toMillis(m.created) });
    }
    return out;
  }

  private toMillis(t: FirestoreMessageSummary['updated']): number {
    if (!t) return 0;
    if (t instanceof Date) return t.getTime();
    if (typeof t.toMillis === 'function') return t.toMillis();
    return 0;
  }

  // applyPage skips-if-exists; applyHydration overwrites. Hydration only
  // runs for non-baseline ids (see collectIdsToHydrate), so a page-fetch
  // body for a baseline-known id stays put — only post-baseline ids ever
  // get the "freshest from Firestore" overwrite.
  private applyPage(
    page: { messages: Message[]; nextCursor: string; hasMore: boolean },
    kind: 'initial' | 'older',
  ): void {
    const s = this.state$$.value;
    const map = new Map(s.messages);
    for (const m of page.messages) {
      if (m.messageId && !map.has(m.messageId)) {
        map.set(m.messageId, m);
      }
    }
    const orderedIds = this.resortDesc(map);
    const nextCursor = page.nextCursor || null;
    this.state$$.next({
      ...s,
      messages: map,
      orderedIds,
      nextCursor,
      hasMore: page.hasMore && !!nextCursor,
      lastPageLandedAt: Date.now(),
      loadingFirstPage: kind === 'initial' ? false : s.loadingFirstPage,
      loadingOlder: kind === 'older' ? false : s.loadingOlder,
    });
    // Keep one chunk ahead of the user: after the initial page lands, prefetch
    // the next page so a scroll-up does not need to wait on a round trip.
    if (kind === 'initial') {
      this.initialPageLanded = true;
      if (this.latestFsSnapshot.length > 0) this.drainTick$$.next();
      if (this.canPrefetchOlder()) this.loadOlder$$.next('prefetch');
    }
  }

  private applyHydration(newMsgs: Message[]): void {
    if (newMsgs.length === 0) return;
    const s = this.state$$.value;
    const map = new Map(s.messages);
    for (const m of newMsgs) {
      if (!m.messageId) continue;
      map.set(m.messageId, m);
    }
    const orderedIds = this.resortDesc(map);
    this.state$$.next({ ...s, messages: map, orderedIds });
  }

  // Newest-at-index-0 ordering is load-bearing: inbox-messages.component's
  // latestMessageCopy / automaticLoadingOpenAIReply effects key off messages[0]
  // as "the latest message". Older-page loads append to the tail, never the head.
  private resortDesc(map: Map<string, Message>): string[] {
    return Array.from(map.keys()).sort((a, b) => {
      const ta = map.get(a)?.created?.getTime() ?? 0;
      const tb = map.get(b)?.created?.getTime() ?? 0;
      return tb - ta;
    });
  }
}
